import { Collapse, Stack, styled } from '@ethings/ui-toolkit';
import { Box, useTheme } from '@mui/material';
import { ComponentProps, FC, forwardRef, useState, type JSX } from 'react';
import { DrawerSize } from './event-history.types';

interface Props {
  historyDrawerContent: JSX.Element;
  isDrawerOpen: boolean;
  setIsTransitioning: (isTransitioning: boolean) => void;
  twoWayCollapseContent: JSX.Element;
}

interface StyledCollapseProps extends ComponentProps<typeof Collapse> {
  isDrawerOpen: boolean;
  width: string;
}

const StyledCollapseBase = styled(Collapse)({
  height: 'inherit',
  overflowY: 'auto',
  position: 'absolute',
  scrollbarWidth: 'none',
  zIndex: 1100,
  '& .MuiCollapse-wrapper': {
    justifyContent: 'center',
  },
  '& .MuiCollapse-wrapperInner': {
    width: '100%',
  },
  '&.collapsed .MuiCollapse-wrapperInner': {
    width: 'auto',
  },
  '::-webkit-scrollbar': {
    display: 'none',
  },
});

const StyledCollapse = forwardRef<HTMLDivElement, StyledCollapseProps>(
  ({ isDrawerOpen, width, style, className, ...props }, ref) => {
    const theme = useTheme();
    return (
      <StyledCollapseBase
        ref={ref}
        {...props}
        style={{
          background: theme.palette.background.paper,
          boxShadow: theme.shadows[4],
          width: `min(${width}, 100%)`,
          ...style,
        }}
        className={`${className || ''} ${!isDrawerOpen ? 'collapsed' : ''}`}
      />
    );
  },
);

StyledCollapse.displayName = 'StyledCollapse';

const OPEN_WIDTH = 'calc(100% - 72px) !important'; // TODO: TP-7045 get width(72px) from theme

export const EventHistoryTablet: FC<Props> = ({
  historyDrawerContent,
  isDrawerOpen,
  setIsTransitioning,
  twoWayCollapseContent,
}) => {
  const [width, setWidth] = useState(isDrawerOpen ? OPEN_WIDTH : 'auto');

  return (
    <Stack direction="row" height="100%" width="100%">
      <StyledCollapse
        collapsedSize={DrawerSize.CLOSED}
        in={isDrawerOpen}
        onEnter={() => setWidth(OPEN_WIDTH)}
        onEntered={() => setIsTransitioning(false)}
        onEntering={() => setIsTransitioning(true)}
        onExited={() => setIsTransitioning(false)}
        onExiting={() => {
          setWidth('auto');
          setIsTransitioning(true);
        }}
        orientation="horizontal"
        width={width}
        isDrawerOpen={isDrawerOpen}
      >
        {historyDrawerContent}
      </StyledCollapse>
      <Box sx={{ flex: [1, 1], height: 'inherit', ml: `${DrawerSize.CLOSED}px` }}>
        {twoWayCollapseContent}
      </Box>
    </Stack>
  );
};
